import styles from "../RegistrationPage.module.scss";
import classNames from "classnames";
import { useForm, Path, Resolver } from "react-hook-form";
import { useRef, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { formatTime } from "utils/utils";

import timerIcon from "../images/timer.svg";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

interface RegCodeFormProps {
  email: string;
  error: string;
  onCodeSubmit: (code: string) => Promise<void>;
  onResendCode: () => Promise<void>;
  sourcePage: "reg" | "auth" | "regCode";
}

interface RegCodeFormValues {
  email: string;
  name?: string;
  codeNumber: string[];
}

const schema = yup.object({
  email: yup
    .string()
    .trim()
    .required("Обязательное поле ввода")
    .email("Некорректный формат почты"),
  name: yup.string(),
  codeNumber: yup
    .array()
    .of(yup.string().required("Обязательное поле ввода"))
    .test("is-filled", "Все поля должны быть заполнены", (value) => {
      return value && value.length === 4 && value.every((v) => v !== "");
    })
    .required("Код обязателен"),
});

export const RegCodeForm: React.FC<RegCodeFormProps> = ({
  email,
  error,
  sourcePage: _sourcePage,
  onCodeSubmit,
  onResendCode,
}) => {
  const inputsRef = useRef<HTMLInputElement[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoadingSpiner, setLoadingSpiner] = useState(false);

  const navigate = useNavigate();
  const [timer, setTimer] = useState(60);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [inputCode, setInputCode] = useState<string[]>(["", "", "", ""]);

  // ===== Таймер =====
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);

      return () => clearInterval(interval);
    } else {
      setIsResendDisabled(false);
    }
  }, [timer]);

  const handleResendCode = async () => {
    if (isResendDisabled) return;

    try {
      setLoadingSpiner(true);
      setErrorMessage(null);
      await onResendCode();
      setTimer(60);
      setIsResendDisabled(true);
    } catch {
      setErrorMessage("Не удалось отправить код. Попробуйте позже.");
    } finally {
      setLoadingSpiner(false);
    }
  };

  // ===== React Hook Form =====
  type FormValues = yup.InferType<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<FormValues>({
    resolver: yupResolver(schema) as unknown as Resolver<RegCodeFormValues>,
    defaultValues: { email, codeNumber: ["", "", "", ""] },
  });

  // ===== Сабмит кода =====
  const onSubmit = useCallback(
    async (data: FormValues) => {
      setLoadingSpiner(true);
      setErrorMessage(null);

      const code = (data.codeNumber ?? []).join("");

      try {
        await onCodeSubmit(code);
      } catch (err: any) {
        const message =
          typeof err === "string"
            ? err
            : err?.message || "Ошибка, попробуйте снова.";

        if (message.includes("Пользователь не найден")) {
          navigate("/account");
          return;
        }

        setErrorMessage(message);
      } finally {
        setLoadingSpiner(false);
      }
    },
    [navigate, onCodeSubmit]
  );

  // ===== Ручной ввод цифр =====
  const handleChange = (value: string, index: number) => {
    setErrorMessage(null);

    const digit = value.replace(/\D/g, "").slice(0, 1);

    const code = watch("codeNumber") || ["", "", "", ""];
    const newCode = [...code];
    newCode[index] = digit;
    setValue(`codeNumber.${index}` as Path<FormValues>, digit);

    const newInputCode = [...inputCode];
    newInputCode[index] = digit;
    setInputCode(newInputCode);

    if (digit && index < newCode.length - 1) {
      inputsRef.current[index + 1]?.focus();
    }

    if (newInputCode.every((v) => v !== "")) {
      handleSubmit(onSubmit)();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Enter" && index === 3) {
      e.preventDefault();
      const code = watch("codeNumber") || ["", "", "", ""];
      if (code.every((v) => v !== "")) {
        handleSubmit(onSubmit)();
      }
      return;
    }

    if (e.ctrlKey || e.metaKey) {
      return;
    }

    if (e.key === "Backspace") {
      const currentValue = (e.currentTarget.value || "").toString();
      if (!currentValue && index > 0) {
        setValue(`codeNumber.${index - 1}` as Path<FormValues>, "");
        const newInputCode = [...inputCode];
        newInputCode[index - 1] = "";
        setInputCode(newInputCode);
        inputsRef.current[index - 1]?.focus();
        e.preventDefault();
      }
      return;
    }

    if (
      !/^\d$/.test(e.key) &&
      !["Tab", "ArrowLeft", "ArrowRight"].includes(e.key)
    ) {
      e.preventDefault();
    }
  };

  // ===== Вставка по Ctrl+V =====
  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    index: number
  ) => {
    e.preventDefault();
    setErrorMessage(null);

    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;

    const digits = pasted.slice(0, 4 - index).split("");

    const code = watch("codeNumber") || ["", "", "", ""];
    const newCode = [...code];
    const newInput = [...inputCode];

    for (let i = 0; i < digits.length && index + i < 4; i++) {
      newCode[index + i] = digits[i];
      newInput[index + i] = digits[i];
      setValue(`codeNumber.${index + i}` as Path<FormValues>, digits[i]);
    }

    setInputCode(newInput);

    const nextEmpty = newInput.findIndex((v, i) => v === "" && i > index);
    if (nextEmpty !== -1) {
      inputsRef.current[nextEmpty]?.focus();
    } else {
      const lastIndex = Math.min(3, index + digits.length - 1);
      inputsRef.current[lastIndex]?.focus();

      if (newInput.every((v) => v !== "")) {
        handleSubmit(onSubmit)();
      }
    }
  };

  // ===== Enter вне инпутов кода =====
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        const target = e.target as HTMLElement;

        if (
          target.tagName !== "INPUT" ||
          target.getAttribute("type") !== "tel"
        ) {
          e.preventDefault();
          const code = watch("codeNumber") || ["", "", "", ""];
          if (code.every((v) => v !== "")) {
            handleSubmit(onSubmit)();
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleSubmit, watch, onSubmit]);

  const finalErrorText = errorMessage || error || "";

  return (
    <>
      <form
        className={classNames(styles.form, styles.formgap_2)}
        onSubmit={handleSubmit(onSubmit)}
        style={{ marginBottom: "167px" }}
      >
        <h2 className={styles.form_title}>Здравствуйте</h2>

        <label
          htmlFor="mail"
          className={classNames(styles.form_emailLabel, styles.inputIcon)}
        >
          Выслали код на адрес
          <input
            id="mail"
            className={classNames(styles.input, styles.emailInput)}
            {...register("email")}
          />
          {errors.email && (
            <p style={{ color: "red", fontSize: "12px", fontWeight: 300 }}>
              {errors.email?.message}
            </p>
          )}
        </label>

        <div className={styles.codeInputsWrapper}>
          <div className={styles.form_codeInputs}>
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index}>
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]"
                  {...register(`codeNumber.${index}` as Path<FormValues>)}
                  maxLength={1}
                  className={styles.form_codeInputItem}
                  value={inputCode[index]}
                  placeholder="_"
                  ref={(el) => {
                    if (el) {
                      inputsRef.current[index] = el;
                    }
                  }}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={(e) => handlePaste(e, index)}
                  autoComplete="one-time-code"
                />
              </div>
            ))}
          </div>

          <div className={styles.errorContainer}>
            {finalErrorText && (
              <p className={styles.errorContainerText}>{finalErrorText}</p>
            )}
          </div>
        </div>

        <button
          type="submit"
          className={styles.form_submitBtn}
          disabled={isLoadingSpiner}
        >
          {isLoadingSpiner ? "Загрузка..." : "Войти"}
        </button>

        <div className={styles.timer}>
          <div className={styles.timerIcon}>
            <img src={timerIcon} alt="timerIcon" />
          </div>
          <div className={styles.timerCounter}>{formatTime(timer)}</div>
        </div>

        <p className={styles.form_sendCodeAgain}>
          Не пришёл код?
          <button
            className={classNames(
              styles.form_sendCodeAgainBtn,
              isResendDisabled && styles.disabled
            )}
            onClick={handleResendCode}
            disabled={isResendDisabled || isLoadingSpiner}
            type="button"
          >
            {isLoadingSpiner ? (
              <span className={styles.spinner}></span>
            ) : (
              "Отправить код снова"
            )}
          </button>
        </p>
      </form>
    </>
  );
};
